import { useEffect, useState } from 'react'

import { earliestFutureMs } from './socialCredits'

/** Longest single timer: setTimeout overflows above ~24.8 days. */
const MAX_TIMEOUT_MS = 2_147_483_647
/** While a moment is ahead, the clock ticks at most this far apart so "in 40 minutes" stays true. */
const TICK_MS = 60_000

/**
 * The current time for cooldown and auto-resume copy (E7). Re-reads the
 * clock whenever the watched moments change (a new cooldown after an update
 * is measured from now, not from mount), ticks once a minute while one is
 * ahead so relative copy ("in 40 minutes") stays current, and re-renders when
 * the earliest one passes so a disabled "Update now" re-enables on its own.
 * Null or past values set no timer. Client clock skew is fine: the server's
 * 429 is the backstop.
 */
export function useNow(
  until: string | null | undefined | ReadonlyArray<string | null | undefined>,
): number {
  const [now, setNow] = useState(() => Date.now())
  const values = Array.isArray(until) ? until : [until as string | null | undefined]
  const valuesKey = values.map((value) => value ?? '').join('|')
  const target = earliestFutureMs(values, now)

  useEffect(() => {
    setNow(Date.now())
  }, [valuesKey])

  useEffect(() => {
    if (target === null) {
      return undefined
    }
    const untilTarget = Math.max(0, target - Date.now()) + 50
    const delay = Math.min(untilTarget, TICK_MS, MAX_TIMEOUT_MS)
    const timer = setTimeout(() => setNow(Date.now()), delay)
    return () => clearTimeout(timer)
  }, [target, now])

  return now
}
